// OrderDetailsModal.jsx
//
// Read-only "View Details" entry point opened from the booking-table
// three-dot menu (separate from the full Edit flow). Tabbed: Order,
// Service, Inventory, Payment — Payment is the one ops actually asked for,
// since online-payment/gateway ids weren't surfaced anywhere in the admin
// panel before.
import React, { useMemo, useState } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import OrderCancellationFee from "../CancellationPolicy/OrderCancellationFee";

const GET_ORDER_INVENTORY_RO = gql`
  query getOrderInventoryReadOnly($data: OrderInventoryInput!) {
    getOrderInventory(data: $data) {
      status
      message
      inventories {
        id
        serviceId
        inventoryId
        serviceName
        categoryName
        serviceCount
        parts
        scanned
        isScanRequired
        scannedInventoryId
        productSelectionMode
        alternateGroupId
        reversed
        reversedAt
        reversedReason
        isManualEntry
        manualCount
        recipeDefaultCount
        extraCost
        deductionGap
        deductionGapDetail
        scannedInventoryDetails {
          status
          quantity
          usage
          employeeId
          assignedDate
          verifiedDate
        }
      }
    }
  }
`;

const ADMIN_REVERSE_ORDER_INVENTORY = gql`
  mutation adminReverseOrderInventory($data: AdminReverseOrderInventoryInput!) {
    adminReverseOrderInventory(data: $data) {
      status
      message
      reversedCount
    }
  }
`;

const ADMIN_RESTORE_ORDER_INVENTORY_ITEM = gql`
  mutation adminRestoreOrderInventoryItem($data: AdminRestoreOrderInventoryItemInput!) {
    adminRestoreOrderInventoryItem(data: $data) {
      status
      message
    }
  }
`;

const GET_BOOKING_AUDIT_LOGS_RO = gql`
  query getBookingAuditLogsReadOnly($orderId: ID!) {
    getBookingAuditLogs(orderId: $orderId) {
      status
      message
      audits {
        id
        changedBy
        reason
        details
        createdAt
      }
    }
  }
`;

// Every attempt at paying for this booking, not just the one that succeeded
// — a customer who fails on try 1 and pays on try 2 leaves two rows here.
const GET_ORDER_PAYMENT_ATTEMPTS = gql`
  query getOrderPaymentAttempts($orderId: ID!) {
    getOrderPaymentAttempts(orderId: $orderId) {
      status
      message
      attempts {
        id
        orderId
        paymentId
        amount
        status
        attemptCount
        lastError
        refundId
        refundStatus
        refundAmount
        refundCreatedAt
        refundArn
        createdAt
        isLinkedToOrder
      }
    }
  }
`;

const TABS = [
  { key: "order", label: "Order Details" },
  { key: "service", label: "Service Details" },
  { key: "inventory", label: "Inventory Details" },
  { key: "payment", label: "Payment Details" },
];

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-[#2a2a2a] text-sm">
    <span className="text-gray-400">{label}</span>
    <span className="text-white text-right break-words">
      {value === null || value === undefined || value === "" ? "—" : value}
    </span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 className="text-[#FBB033] font-semibold text-sm uppercase tracking-wide mt-4 mb-2 first:mt-0">
    {children}
  </h4>
);

const OrderDetailsModal = ({ booking, onClose }) => {
  const [activeTab, setActiveTab] = useState("order");
  const [selectedForReversal, setSelectedForReversal] = useState([]);

  const [fetchInventory, { data: invData, loading: invLoading, called: invCalled }] =
    useLazyQuery(GET_ORDER_INVENTORY_RO, {
      variables: { data: { orderId: booking?.id } },
      fetchPolicy: "network-only",
    });

  const [reverseInventory, { loading: reverseLoading }] = useMutation(
    ADMIN_REVERSE_ORDER_INVENTORY,
    {
      onCompleted: () => {
        setSelectedForReversal([]);
        fetchInventory();
      },
    },
  );

  const [restoreInventoryItem, { loading: restoreLoading }] = useMutation(
    ADMIN_RESTORE_ORDER_INVENTORY_ITEM,
    { onCompleted: () => fetchInventory() },
  );

  const [fetchAudits, { data: auditData, loading: auditLoading, called: auditCalled }] =
    useLazyQuery(GET_BOOKING_AUDIT_LOGS_RO, {
      variables: { orderId: booking?.id },
      fetchPolicy: "network-only",
    });

  const [
    fetchPaymentAttempts,
    { data: attemptsData, loading: attemptsLoading, called: attemptsCalled },
  ] =
    useLazyQuery(GET_ORDER_PAYMENT_ATTEMPTS, {
      variables: { orderId: booking?.id },
      fetchPolicy: "network-only",
    });

  const hasAddOn = (booking?.orderDetail || []).some((s) => s.addOnCount > 0);

  const selectTab = (key) => {
    setActiveTab(key);
    if (key === "inventory" && !invCalled) fetchInventory();
    // Audit log feeds the payment tab (manual payments) and the service tab
    // (add-on summary + admin corrections, which can't be spotted from the
    // service rows alone) - load it for whichever opens first.
    if ((key === "payment" || key === "service") && !auditCalled) {
      fetchAudits();
    }
    if (key === "payment" && !attemptsCalled) fetchPaymentAttempts();
  };

  const inventories = invData?.getOrderInventory?.inventories || [];

  // Per-category rollup: how many parts the service recipe expected to be
  // deducted vs. how many were actually scanned/auto-cut off the employee's
  // stock, so a mismatch is visible without counting rows by hand.
  const inventoryByCategory = useMemo(() => {
    const map = {};
    inventories.forEach((inv) => {
      const key = inv.inventoryId || inv.categoryName;
      if (!map[key]) {
        map[key] = {
          categoryId: key,
          categoryName: inv.categoryName || "Unknown",
          expectedParts: 0,
          actualParts: 0,
          // Each row carries only its own extra cost (see extraCost on
          // OrderInventory) - sum them for the category's total.
          extraCost: 0,
        };
      }
      const parts = Number(inv.parts) || 0;
      // Removed before Start or credited back: not expected any more, so
      // counting it here showed a gap that isn't one.
      if (!inv.reversed) {
        map[key].expectedParts += parts;
      }
      if (inv.scanned && !inv.reversed) {
        map[key].actualParts += parts;
      }
      if (inv.extraCost && !inv.reversed) {
        map[key].extraCost += Number(inv.extraCost);
      }
    });
    return Object.values(map).sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  }, [inventories]);

  if (!booking) return null;

  const pending = Math.max(
    Number(booking.price || 0) - Number(booking.amountPaid || 0),
    0,
  );

  // Each on-site add-on edit (editBooking) records what was added and what it
  // did to the price, oldest first.
  const addOnAudits = (auditData?.getBookingAuditLogs?.audits || [])
    .filter((a) => a.details && a.details.onSiteAddOn)
    // createdAt arrives as a ms-timestamp string (or an ISO string).
    .sort(
      (a, b) =>
        new Date(Number(a.createdAt) || a.createdAt) -
        new Date(Number(b.createdAt) || b.createdAt),
    );

  const auditTime = (v) => new Date(Number(v) || v);
  // Admin corrections on a started booking (EditBooking "Edit / correction")
  // - kept separate from add-ons.
  const correctionAudits = (auditData?.getBookingAuditLogs?.audits || [])
    .filter((a) => a.details && a.details.adminCorrection)
    .sort((a, b) => auditTime(a.createdAt) - auditTime(b.createdAt));

  const manualPayments = (auditData?.getBookingAuditLogs?.audits || []).filter(
    (a) => a.details && a.details.source === "ADMIN_MANUAL_QR",
  );

  const paymentAttempts = attemptsData?.getOrderPaymentAttempts?.attempts || [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] flex flex-col bg-[#1a1a1a] text-white rounded-xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a]">
          <h3 className="text-lg font-bold text-[#FBB034]">
            Booking {booking.bookingId || booking.id} — Details
          </h3>
          <button
            className="text-gray-400 hover:text-white text-xl leading-none"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 px-5 pt-3">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`px-3 py-1.5 rounded text-sm font-semibold ${
                activeTab === tab.key
                  ? "bg-[#FBB034] text-black"
                  : "bg-[#0c0b0b] text-white border border-[#333]"
              }`}
              onClick={() => selectTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="px-5 py-4 overflow-y-auto">
          {activeTab === "order" && (
            <div>
              <SectionTitle>Booking</SectionTitle>
              <Row label="Booking ID" value={booking.bookingId} />
              <Row label="Status" value={booking.status} />
              <Row label="Verification Status" value={booking.verificationStatus} />
              <Row label="Booking Type" value={booking.bookingType} />
              <Row
                label="Date / Time"
                value={
                  booking.bookingDate
                    ? `${booking.bookingDate}${booking.timeSlot ? `, ${booking.timeSlot}` : ""}`
                    : "—"
                }
              />
              <Row label="Address" value={booking.address} />
              <Row label="Pincode" value={booking.pincode} />

              <SectionTitle>Customer</SectionTitle>
              <Row label="Name" value={booking.user?.fullName} />
              <Row label="Phone" value={booking.user?.contactNumber} />
              <Row label="Blocked" value={booking.user?.isBlocked ? "Yes" : "No"} />

              <SectionTitle>Employee</SectionTitle>
              <Row label="Assigned Employee" value={booking.employee?.fullName} />
              <Row label="Dummy Employee" value={booking.employee?.isDummy ? "Yes" : "No"} />

              {booking.adminRemark && (
                <>
                  <SectionTitle>Admin Remark</SectionTitle>
                  <p className="text-sm text-white bg-[#2a2a2a] p-3 rounded-lg whitespace-pre-wrap">
                    {booking.adminRemark}
                  </p>
                </>
              )}

              {booking.issueType && (
                <>
                  <SectionTitle>Reported Issue</SectionTitle>
                  <Row label="Issue Type" value={booking.issueType} />
                  <Row label="Description" value={booking.issueDescription} />
                  <Row
                    label="Resolved"
                    value={booking.issueResolvedAt ? "Yes" : "No"}
                  />
                </>
              )}
            </div>
          )}

          {activeTab === "service" && (
            <div>
              {hasAddOn && (
                <div className="mb-4 rounded-lg border border-violet-500/40 bg-violet-500/10 p-3 text-sm">
                  <div className="font-semibold text-violet-300 mb-2">
                    ➕ On-site add-ons
                  </div>
                  {auditLoading && (
                    <p className="text-gray-400 text-xs">Loading…</p>
                  )}
                  {!auditLoading && addOnAudits.length === 0 && (
                    <p className="text-gray-400 text-xs">
                      Added on-site, but no price summary was recorded for it
                      (added before this was tracked).
                    </p>
                  )}
                  {addOnAudits.map((a) => {
                    const info = a.details.onSiteAddOn;
                    return (
                      <div
                        key={a.id}
                        className="py-2 border-t border-violet-500/20 first:border-t-0 first:pt-0"
                      >
                        <div className="text-white font-medium">{info.services}</div>
                        <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <div className="text-gray-400">Extra</div>
                            <div className="text-white font-semibold">
                              +₹{info.extraAmount}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-400">Before</div>
                            <div className="text-white">₹{info.oldTotal}</div>
                          </div>
                          <div>
                            <div className="text-gray-400">New total</div>
                            <div className="text-violet-300 font-semibold">
                              ₹{info.newTotal}
                            </div>
                          </div>
                        </div>
                        <div className="mt-1 text-[11px] text-gray-400">
                          By {info.addedByType === "EMPLOYEE" ? "professional" : "admin"}
                          {info.approvedVia === "CUSTOMER_CODE"
                            ? " · approved with customer code"
                            : ""}
                          {a.createdAt
                            ? ` · ${new Date(
                                Number(a.createdAt) || a.createdAt,
                              ).toLocaleString("en-IN", {
                                day: "numeric",
                                month: "short",
                                hour: "numeric",
                                minute: "2-digit",
                              })}`
                            : ""}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {correctionAudits.length > 0 && (
                <div className="mb-4 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-sm">
                  <div className="font-semibold text-sky-300 mb-2">
                    ✏️ Admin corrections
                  </div>
                  {correctionAudits.map((a) => {
                    const info = a.details.adminCorrection;
                    const change = Number(info.priceChange) || 0;
                    return (
                      <div
                        key={a.id}
                        className="py-2 border-t border-sky-500/20 first:border-t-0 first:pt-0"
                      >
                        <div className="text-white">{info.reason}</div>
                        <div className="mt-1 text-xs text-gray-300">
                          Price: ₹{info.oldTotal} → ₹{info.newTotal}
                          {change !== 0 && (
                            <span className={change > 0 ? "text-amber-300" : "text-green-300"}>
                              {" "}({change > 0 ? "+" : "−"}₹{Math.abs(change)})
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-[11px] text-gray-400">
                          No customer code · not counted as an add-on
                          {a.createdAt
                            ? ` · ${auditTime(a.createdAt).toLocaleString("en-IN", {
                                day: "numeric",
                                month: "short",
                                hour: "numeric",
                                minute: "2-digit",
                              })}`
                            : ""}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <SectionTitle>Services</SectionTitle>
              {(booking.orderDetail || []).length === 0 && (
                <p className="text-gray-400 text-sm">No services on this booking.</p>
              )}
              {(booking.orderDetail || []).map((svc) => (
                <div
                  key={svc.id}
                  className="bg-[#2a2a2a] rounded-lg p-3 mb-2 text-sm"
                >
                  <div className="flex justify-between font-semibold text-white">
                    <span className="flex items-center gap-2">
                      {svc.name}
                      {svc.addOnCount > 0 && (
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-violet-600 text-white">
                          ➕ Add-on
                        </span>
                      )}
                    </span>
                    <span>Qty: {svc.count ?? 1}</span>
                  </div>
                  {svc.addOnCount > 0 && (
                    <div className="mt-1 text-xs text-violet-300">
                      Added on-site
                      {svc.addOnCount < (svc.count ?? 1)
                        ? ` (${svc.addOnCount} of ${svc.count})`
                        : svc.addOnCount > 1
                          ? ` (x${svc.addOnCount})`
                          : ""}
                      {/* Who / when / approval: see the On-site add-ons box above. */}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-4 mt-1 text-gray-300">
                    <span>Price: ₹{svc.price ?? "—"}</span>
                    <span>Special Price: ₹{svc.specialPrice ?? "—"}</span>
                    <span>First Time Price: ₹{svc.firstTimePrice ?? "—"}</span>
                    <span>Cut Price: ₹{svc.cutPrice ?? "—"}</span>
                    <span>Type: {svc.type || "—"}</span>
                  </div>
                  {svc.completionStatus === "NOT_COMPLETED" && (
                    <div className="mt-2 pt-2 border-t border-[#444] text-amber-400 text-xs">
                      Not completed — {svc.incompleteReason || "no reason given"}
                    </div>
                  )}
                </div>
              ))}

              <SectionTitle>Pricing Summary</SectionTitle>
              <Row label="Price" value={`₹${booking.price ?? "—"}`} />
              <Row label="Total Price" value={`₹${booking.totalPrice ?? "—"}`} />
              <Row
                label="Admin Price Adjustment"
                value={
                  booking.adminPriceAdjustment
                    ? `₹${booking.adminPriceAdjustment} (${booking.adminPriceAdjustmentReason || "no reason given"})`
                    : "—"
                }
              />
              <Row label="Coupon Code" value={booking.couponCode} />
              <Row
                label="Coupon Discount"
                value={booking.couponDiscount ? `₹${booking.couponDiscount}` : "—"}
              />

              {/* What the professional earns from this booking */}
              <SectionTitle>Professional Pay</SectionTitle>
              {(() => {
                const salaryOnly = ["MONTHLY", "PER_DAY"].includes(booking.employee?.salaryType);
                const inr = (v) => (v != null ? `₹${v}` : "—");
                return (
                  <>
                    <Row
                      label="Extra Travel Km"
                      value={booking.extraKilometers ? `${booking.extraKilometers} km` : "—"}
                    />
                    <Row label="Travel Allowance" value={inr(booking.travelCommission)} />
                    <Row
                      label="Booking Commission"
                      value={
                        salaryOnly
                          ? `Not paid - salary only (${booking.employee.salaryType})`
                          : booking.baseCommission != null
                            ? `₹${booking.baseCommission}${booking.commissionPercentageUsed != null ? ` (${booking.commissionPercentageUsed}%)` : ""}`
                            : "—"
                      }
                    />
                    <Row label="Out-of-hours Bonus" value={inr(booking.bonusCommission)} />
                    <Row
                      label="Total Paid for Booking"
                      value={
                        salaryOnly
                          ? inr((booking.travelCommission || 0) + (booking.bonusCommission || 0))
                          : inr(booking.totalCommission)
                      }
                    />
                    <Row label="Pay Status" value={booking.commissionStatus} />
                  </>
                );
              })()}
            </div>
          )}

          {activeTab === "inventory" && (
            <div>
              <SectionTitle>Expected vs Deducted (by category)</SectionTitle>
              {invLoading && (
                <p className="text-gray-400 text-sm">Loading inventory…</p>
              )}
              {!invLoading && inventoryByCategory.length === 0 && (
                <p className="text-gray-400 text-sm">
                  No inventory records for this order.
                </p>
              )}
              {!invLoading && inventoryByCategory.length > 0 && (
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-gray-400 border-b border-[#2a2a2a]">
                      <tr>
                        <th className="py-1.5 pr-4 font-medium">Category</th>
                        <th className="py-1.5 pr-4 font-medium">Should Deduct</th>
                        <th className="py-1.5 pr-4 font-medium">Actually Deducted</th>
                        <th className="py-1.5 pr-4 font-medium">Gap</th>
                        <th className="py-1.5 pr-4 font-medium">Extra Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventoryByCategory.map((cat) => {
                        const gap = cat.expectedParts - cat.actualParts;
                        return (
                          <tr key={cat.categoryId} className="border-b border-[#2a2a2a]">
                            <td className="py-1.5 pr-4 text-white">{cat.categoryName}</td>
                            <td className="py-1.5 pr-4 text-gray-300">{cat.expectedParts}</td>
                            <td className="py-1.5 pr-4 text-gray-300">{cat.actualParts}</td>
                            <td
                              className={`py-1.5 pr-4 font-semibold ${
                                gap === 0 ? "text-green-400" : "text-amber-400"
                              }`}
                            >
                              {gap === 0 ? "None" : gap}
                            </td>
                            <td className="py-1.5 pr-4 text-gray-300">
                              {cat.extraCost > 0 ? `₹${cat.extraCost}` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-between mt-4 mb-2">
                <SectionTitle>Scan Status (per unit)</SectionTitle>
                {selectedForReversal.length > 0 && (
                  <button
                    type="button"
                    disabled={reverseLoading}
                    onClick={() =>
                      reverseInventory({
                        variables: {
                          data: {
                            orderId: booking.id,
                            orderInventoryIds: selectedForReversal,
                            reason: "ADMIN_CORRECTION",
                          },
                        },
                      })
                    }
                    className="px-3 py-1 rounded text-xs font-semibold bg-[#FBB034] text-black disabled:opacity-50"
                  >
                    {reverseLoading
                      ? "Crediting back…"
                      : `Credit back ${selectedForReversal.length} unused item(s)`}
                  </button>
                )}
              </div>
              {!invLoading && inventories.length === 0 && (
                <p className="text-gray-400 text-sm">
                  No inventory records for this order.
                </p>
              )}
              {!invLoading &&
                inventories.map((inv) => {
                  const canReverse = inv.scanned && !inv.reversed;
                  const checked = selectedForReversal.includes(inv.id);
                  return (
                    <div
                      key={inv.id}
                      className="flex justify-between items-center py-2 border-b border-[#2a2a2a] text-sm gap-2"
                    >
                      <div className="flex items-center gap-2">
                        {canReverse && (
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              setSelectedForReversal((prev) =>
                                e.target.checked
                                  ? [...prev, inv.id]
                                  : prev.filter((id) => id !== inv.id),
                              )
                            }
                          />
                        )}
                        <div>
                          <div className="text-white">{inv.categoryName}</div>
                          <div className="text-gray-400 text-xs">
                            {inv.serviceName} — {inv.serviceCount}
                            {inv.productSelectionMode === "ALTERNATE_FALLBACK" && (
                              <span className="ml-2 text-amber-400">Alternate not chosen</span>
                            )}
                            {inv.productSelectionMode === "EMPLOYEE_OVERRIDDEN_COUNT" && (
                              <span className="ml-2 text-amber-400">
                                Exceeded recipe default ({inv.recipeDefaultCount})
                                {inv.extraCost > 0 ? ` +₹${inv.extraCost}` : ""}
                              </span>
                            )}
                            {inv.productSelectionMode === "EMPLOYEE_ADDED_ADHOC" && (
                              <span className="ml-2 text-amber-400">
                                {inv.serviceName === "Added during service"
                                  ? "Added by employee after start, not re-approved by customer"
                                  : "Added by employee, not in recipe"}
                                {inv.extraCost > 0 ? ` +₹${inv.extraCost}` : ""}
                              </span>
                            )}
                            {inv.productSelectionMode === "ADMIN_ADDED_ADHOC" && (
                              <span className="ml-2 text-amber-400">Added by admin</span>
                            )}
                          </div>
                          {inv.scannedInventoryDetails && (
                            <div className="text-gray-500 text-[11px]">
                              Physical unit status: {inv.scannedInventoryDetails.status}
                              {" · usage "}
                              {inv.scannedInventoryDetails.usage}/
                              {inv.scannedInventoryDetails.quantity}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {inv.reversed ? (
                          <span className="text-purple-400 font-semibold block">
                            {{
                              EMPLOYEE_REMOVED_PRESTART: "Removed by employee",
                              // Never deducted - nothing was credited back.
                              EMPLOYEE_REMOVED_AFTER_START: "Removed by employee after start",
                              EMPLOYEE_REMOVED_ADDON: "Removed by employee (add-on review)",
                              ALTERNATE_NOT_USED: "Not used (other option used)",
                              SERVICE_NOT_COMPLETED: "Not used (service not done)",
                            }[inv.reversedReason] || "Credited back"}
                            <span className="block text-[10px] text-gray-500 font-normal">
                              {inv.reversedReason}
                            </span>
                            {inv.reversedReason === "EMPLOYEE_REMOVED_PRESTART" && (
                              <button
                                type="button"
                                disabled={restoreLoading}
                                onClick={() =>
                                  restoreInventoryItem({
                                    variables: { data: { orderInventoryId: inv.id } },
                                  })
                                }
                                className="mt-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#FBB034] text-black disabled:opacity-50"
                              >
                                {restoreLoading ? "Restoring…" : "Restore"}
                              </button>
                            )}
                          </span>
                        ) : inv.deductionGap ? (
                          // Should have come out of the professional's stock
                          // but didn't - and why.
                          <span className="text-orange-400 font-semibold block">
                            Not deducted
                            <span className="block text-[10px] font-normal">
                              {{
                                NO_STOCK: "No stock",
                                INSUFFICIENT_STOCK: "Not enough stock",
                                NOT_SCANNED: "Not scanned",
                              }[inv.deductionGap] || inv.deductionGap}
                            </span>
                            {inv.deductionGapDetail && (
                              <span className="block text-[10px] text-gray-400 font-normal max-w-[180px]">
                                {inv.deductionGapDetail}
                              </span>
                            )}
                          </span>
                        ) : inv.scanned ? (
                          <span className="text-green-400 font-semibold">Scanned</span>
                        ) : inv.isScanRequired === false ? (
                          <span className="text-blue-400 font-semibold">Auto Cut</span>
                        ) : (
                          <span className="text-amber-400 font-semibold">Pending</span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {activeTab === "payment" && (
            <div>
              <SectionTitle>Payment Summary</SectionTitle>
              <Row label="Payment Type" value={booking.paymentType} />
              <Row label="Payment Status" value={booking.paymentStatus} />
              <Row label="Price" value={`₹${booking.price ?? "—"}`} />
              <Row label="Amount Paid" value={`₹${booking.amountPaid ?? 0}`} />
              <Row label="Amount Pending" value={`₹${pending}`} />
              <Row label="Cash Collected" value={`₹${booking.cashCollected ?? 0}`} />
              <Row
                label="Admin Recorded Amount"
                value={`₹${booking.adminRecordedAmount ?? 0}`}
              />
              <Row
                label="Wallet Amount Used"
                value={booking.walletAmountUsed ? `₹${booking.walletAmountUsed}` : "—"}
              />
              <Row
                label="COD Charges"
                value={booking.codCharges ? `₹${booking.codCharges}` : "—"}
              />
              <Row
                label="Taxable Amount"
                value={booking.taxableAmount ? `₹${booking.taxableAmount}` : "—"}
              />
              <Row label="GST" value={booking.gst ? `₹${booking.gst}` : "—"} />
              <Row label="Invoice Number" value={booking.invoiceNumber} />

              <SectionTitle>Online Payment Attempts (Gateway)</SectionTitle>
              <Row label="Razorpay Order ID" value={booking.razorpayOrderId} />
              <p className="text-xs text-gray-500 mt-1 mb-2">
                Every payment attempt for this booking, oldest first — a
                failed first try followed by a successful retry will show as
                two entries below.
              </p>
              {attemptsLoading && (
                <p className="text-gray-400 text-sm">Loading payment attempts…</p>
              )}
              {!attemptsLoading && paymentAttempts.length === 0 && (
                <p className="text-gray-400 text-sm">
                  No online gateway payment attempts found for this order.
                </p>
              )}
              {!attemptsLoading &&
                paymentAttempts.map((session, idx) => (
                  <div
                    key={session.id}
                    className={`bg-[#2a2a2a] rounded-lg p-3 mb-2 text-sm border-l-4 ${
                      session.isLinkedToOrder
                        ? "border-l-green-500"
                        : session.status === "FAILED"
                          ? "border-l-red-500"
                          : "border-l-gray-500"
                    }`}
                  >
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-white font-semibold">
                        Attempt {idx + 1} — {session.status}
                      </span>
                      <div className="flex items-center gap-2">
                        {session.isLinkedToOrder && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-600 text-white">
                            Linked to this booking
                          </span>
                        )}
                        <span className="text-gray-400 text-xs">
                          {session.createdAt
                            ? new Date(session.createdAt).toLocaleString()
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-gray-300 text-xs">
                      <span>Razorpay Payment ID: {session.paymentId || "—"}</span>
                      <span>Razorpay Order ID: {session.orderId || "—"}</span>
                      <span>Amount: {session.amount ? `₹${session.amount}` : "—"}</span>
                      <span>Attempt Count: {session.attemptCount ?? "—"}</span>
                    </div>
                    {session.lastError && (
                      <div className="text-red-400 text-xs mt-2">
                        Error: {session.lastError}
                      </div>
                    )}
                    {session.refundId && (
                      <div className="mt-2 pt-2 border-t border-[#444] text-xs text-gray-300">
                        <div className="text-[#FBB033] font-semibold mb-1">Refund</div>
                        <div>Refund ID: {session.refundId}</div>
                        <div>Refund Status: {session.refundStatus || "—"}</div>
                        <div>
                          Refund Amount:{" "}
                          {session.refundAmount ? `₹${session.refundAmount}` : "—"}
                        </div>
                        <div>Refund ARN: {session.refundArn || "—"}</div>
                        <div>
                          Refund Created At:{" "}
                          {session.refundCreatedAt
                            ? new Date(session.refundCreatedAt).toLocaleString()
                            : "—"}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

              {["Cancelled", "Declined"].includes(booking.status) && (
                <>
                  <SectionTitle>Cancellation Fee</SectionTitle>
                  <OrderCancellationFee orderId={booking.id} />
                </>
              )}

              <SectionTitle>Manually Recorded Payments</SectionTitle>
              {auditLoading && (
                <p className="text-gray-400 text-sm">Loading payment history…</p>
              )}
              {!auditLoading && manualPayments.length === 0 && (
                <p className="text-gray-400 text-sm">
                  No manually recorded payments for this order.
                </p>
              )}
              {!auditLoading &&
                manualPayments.map((a) => (
                  <div
                    key={a.id}
                    className="bg-[#2a2a2a] rounded-lg p-3 mb-2 text-sm"
                  >
                    <div className="flex justify-between text-white font-semibold">
                      <span>₹{a.details?.amount ?? "—"}</span>
                      <span className="text-gray-400 text-xs">
                        {a.createdAt ? new Date(a.createdAt).toLocaleString() : ""}
                      </span>
                    </div>
                    {a.details?.referenceId && (
                      <div className="text-gray-300 text-xs mt-1">
                        Ref: {a.details.referenceId}
                      </div>
                    )}
                    {a.details?.note && (
                      <div className="text-gray-300 text-xs mt-1">
                        Note: {a.details.note}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
